import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { loadAllArticles } from '@/services/articleLoader';

import { MainNavigation } from '@/components/MainNavigation';
import { ClusterLinks } from '@/components/ClusterLinks';
import { RelatedSemantic } from '@/components/RelatedSemantic';
import { Footer } from '@/components/Footer';
import { SportsInjector } from '@/components/Sports/SportsInjector';

// New Layout System
import { ArticleLayoutDispatcher } from '@/components/layouts/ArticleLayoutDispatcher';
import { prepareArticleForAIO } from '@/components/layouts/StandardLayout';

export const BlogArticlePage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const [article, setArticle] = useState(null);
  const [allArticles, setAllArticles] = useState([]); // Store all for cluster discovery
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadArticle = async () => {

      try {
        const articles = await loadAllArticles();


        if (isMounted) {
          if (articles && articles.length > 0) {
            setAllArticles(articles); // Save all for cluster linking
            let found = null;

            if (slug) {
              found = articles.find(a => a.slug === slug);
            } else {
              // Try to find by canonical path (for /guides/troubleshooting etc)
              // Ensure we strip trailing slash if needed, or match exact
              found = articles.find(a => a.canonicalPath === location.pathname || a.canonicalPath === location.pathname + '/');
            }

            if (found) {

              setArticle(found);
              setLoading(false);
              return;
            } else {
              console.warn(`⚠️ Article not found for slug: ${slug} or path: ${location.pathname}`);
            }
          }


          setArticle(null);
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ Error loading article:', error);
        if (isMounted) {
          setArticle(null);
          setLoading(false);
        }
      }
    };

    loadArticle();

    return () => {
      isMounted = false;
    };
  }, [slug, location]);

  if (loading) {
    return (
      <>
        <MainNavigation />
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-card/30">
          <div className="text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"
            />
            <p className="text-muted-foreground">Loading article...</p>
          </div>
        </div>
      </>
    );
  }

  if (!article) {
    return (
      <>
        <MainNavigation />
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-card/30">
          <div className="text-center max-w-md">
            <div className="mb-4 inline-flex items-center justify-center w-16 h-16 bg-red-500/10 border border-red-500/30 rounded-full">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Article Not Found</h1>
            <p className="text-muted-foreground mb-6">The article you're looking for doesn't exist.</p>
            <Link to="/blog" className="inline-flex items-center gap-2 px-6 py-2 bg-primary/10 border border-primary/30 rounded-lg font-semibold text-primary hover:bg-primary/20 transition">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>
          </div>
        </div>
      </>
    );
  }

  // Prepare article with defaults
  try {
    const preparedArticle = prepareArticleForAIO(article);

    return (
      <>
        <Helmet>
          <title>{article.title} | XtreamGate Blog</title>
          <meta name="description" content={article.excerpt} />
          <meta name="keywords" content={`iptv, ${article.category}, ${article.title}`} />
          <link rel="canonical" href={`https://xtreamgate.com${article.canonicalPath || '/blog/' + article.slug}`} />
        </Helmet>

        <MainNavigation />

        {/* Sports Injection - Live scores for sports-tagged articles */}
        <div className="max-w-4xl mx-auto px-4 pt-8">
          <SportsInjector article={article} />
        </div>

        {/* Dynamic Layout Dispatcher */}
        <ArticleLayoutDispatcher article={preparedArticle} />

        {/* Internal Linking 2.0: Cluster Discovery */}
        <div className="max-w-7xl mx-auto px-4 pb-12">
          <ClusterLinks currentArticle={article} allArticles={allArticles} />
          {/* v3.5: Semantic + Tag-based Related Articles */}
          <RelatedSemantic currentArticle={article} allArticles={allArticles} />
        </div>

        {/* Footer - appears AFTER all cluster/related sections */}
        <Footer />
      </>
    );
  } catch (error) {
    console.error('Error rendering article:', error);
    return (
      <>
        <MainNavigation />
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-card/30">
          <div className="text-center max-w-md">
            <h1 className="text-3xl font-bold mb-2">Error Loading Article</h1>
            <p className="text-muted-foreground mb-6">{error.message}</p>
            <Link to="/blog" className="inline-flex items-center gap-2 px-6 py-2 bg-primary/10 border border-primary/30 rounded-lg font-semibold text-primary hover:bg-primary/20 transition">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>
          </div>
        </div>
      </>
    );
  }
};
